import { isDefinedArray, isNullOrUndefined, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { configure, observable } from "mobx";

import { apiCallGetAllAdmins } from "../../api/core-api/api-call-get-all-admins";
import et3ReadableDate, {
  getHoursDifference
} from "../../helpers/et3-date-helper";
import {
  getPermissionIDRequired,
  hasSpecificRoutePermission
} from "../../helpers/et3-has-permission";
import getCoreConnectionStore from "./core-connection-store";
import getCoreLoginStore from "./core-login-store";
import getCorePersonalStore from "./core-personal-store";
import getCorePopUpStore from "./core-popup-store";
import getCoreTabStore from "./core-tab-store";
import getCoreTimingStore from "./core-timing-store";
import getCoreUIStore from "./core-ui-store";
import getDashBoardStore from "../dashboard-store";
import getCSRReceiptStore from "../csr-store/csr-receipt-event-count";
import { CUSTOMER_SUPPORT_ROLE } from "../../constants/input-entries-constants";
import { SESSION_HOURS_TIME_LIMIT } from "../../constants/core-constants";
import { SupervisorKhattabatDataStore } from "../supervisor-store/supervisor-khattabat-data-store";

class RootStore {
  allAdmins = observable.box<{ value: string; label: string }[]>([]);
  allCsrAdmins = observable.box<{ value: string; label: string }[]>([]);

  startApp = async () => {
    this.setMobxConfig();

    const userFromStorage = localStorage.getItem("user");
    const userPermissionFromStorage = localStorage.getItem("user_permissions");

    if (!isNullOrUndefined(userFromStorage)) {
      const user = reduce(() => JSON.parse(userFromStorage), {
        id: -1,
        username: "",
        fullName: "",
        role: "",
        lastLogin: et3ReadableDate(Date.now())
      });
      const permissions = reduce(
        () => JSON.parse(userPermissionFromStorage),
        []
      );
      const dateDifferenceInHours = getHoursDifference({
        dateFrom: new Date(user.lastLogin),
        dateTo: new Date()
      });

      getCorePersonalStore().user.set(user);
      getCorePersonalStore().userPermissions.set(permissions);
      getCorePersonalStore().setUserProfilePic();

      const isAnyKhattabaAdmin =
        getCorePersonalStore().isAnyKhattabaAdmin.get();
      let accessState = true;
      // For testing session 1 min
      // if (dateDifferenceInMinutes > 1) {
      const isSessionTimeExceeded =
        dateDifferenceInHours > SESSION_HOURS_TIME_LIMIT;
      if (isSessionTimeExceeded || !accessState) {
        getCorePopUpStore().showAlertMessage("SESSION_EXPIRED");
        await getCoreLoginStore().signOutHandler();
        return false;
      }

      getCoreUIStore().isSideBarVisible.set(false);
      this.initializeTimersAndListenners();
      if (!isAnyKhattabaAdmin) {
        await this.setDashboardCounts();
        await getCSRReceiptStore().getFinalSalary();
      } else {
        await getDashBoardStore().getKhattabatDashboardData();
        await getCorePersonalStore().getAdminVersionStatesFromDatabase();
      }

      if (getCorePersonalStore().user.get().needResetPassword) {
        getCorePopUpStore().openUpdatePasswordPopUp();
      }

      return true;
    } else {
      getCorePersonalStore().user.set({
        id: -1,
        username: "",
        fullName: "",
        role: "",
        lastLogin: "",
        needResetPassword: false
      });
      getCoreUIStore().isSideBarVisible.set(false);
      return false;
    }
  };

  setMobxConfig = () => {
    //this config is to remove the warns "Since strict-mode is enabled,
    //changing (observed) observable values without using an action is not allowed"
    configure({
      enforceActions: "never"
    });
  };

  refreshData = async () => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    if (isAnyKhattabaAdmin) {
      await getDashBoardStore().getKhattabatDashboardData();
      return;
    }
    await getCorePersonalStore().getAdminVersionStatesFromDatabase();
    getCoreUIStore().setIsLoading("ALARMS", true);
    getCoreUIStore().setLatestDataLoading(true);
    await getCorePersonalStore().getMyPermission();
    const userPermissions = getCorePersonalStore().userPermissions.get();
    const hasSupervisorPermission =
      userPermissions.indexOf(getPermissionIDRequired("Supervisor")) > -1;
    const hasDashboardPermission =
      userPermissions.indexOf(getPermissionIDRequired("Alarms")) > -1;
    if (hasSupervisorPermission) {
      await SupervisorKhattabatDataStore().getKhattabatDataCollection();
    }
    if (hasDashboardPermission) {
      await getDashBoardStore().getDashboardData();
    }
    getCoreUIStore().setIsLoading("ALARMS", false);
    getCoreUIStore().setLatestDataLoading(false);
  };

  setDashboardCounts = async () => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    if (isAnyKhattabaAdmin) {
      return;
    }
    await this.refreshData();
    let countsStorage = sessionStorage.getItem("counts");
    const counts = JSON.parse(countsStorage);
    const reportsCount = reduce(() => counts.reportsCount, 0);
    const reportedCount = reduce(() => counts.reportedCount, 0);
    const approvalCount = reduce(() => counts.approvalCount, 0);
    const investigationCasesCount = reduce(
      () => counts.investigationCasesCount,
      0
    );

    const activeTime = reduce(() => counts.activeTime, 0);
    getDashBoardStore().reportsCount.set(reportsCount);
    getDashBoardStore().reportedCount.set(reportedCount);
    getDashBoardStore().approvalCount.set(approvalCount);
    getDashBoardStore().investigationCasesCount.set(investigationCasesCount);
    getDashBoardStore().activeTime.set(activeTime);
  };

  initializeTimersAndListenners = () => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    if (isAnyKhattabaAdmin) {
      getCoreTimingStore().turnOnKhattabaRefreshTimer();
      getCoreTimingStore().monitorKhattabatPages();
    } else {
      getCoreTimingStore().turnOnRefreshTimer();
    }
    getCoreTabStore().onStartListeners();
    getCoreConnectionStore().onStartListeners();
  };

  killAppTimersAndListenners = () => {
    getCoreTimingStore().kilTimers();
    getCoreTabStore().onKillListeners();
    getCoreConnectionStore().onKillListeners();
  };
  getAllAdmins = async () => {
    if (isDefinedArray(this.allAdmins) && this.allAdmins.length > 0) {
      return;
    }
    const hasPermission = hasSpecificRoutePermission("ALL_ADMINS");
    if (!hasPermission) {
      const adminArray = [
        {
          label: getCorePersonalStore().user.get().fullName,
          value: getCorePersonalStore().user.get().id.toString()
        }
      ];
      const newAdminArray = adminArray.concat({
        value: "",
        label: "Choose..."
      });
      this.allAdmins.set(newAdminArray);
      return;
    }
    const { data, status } = await apiCallGetAllAdmins();
    if (status === "OK") {
      const newData = data.map((item) => {
        return { value: item.adminId.toString(), label: item.fullname };
      });
      const newArray = newData.concat({ value: "", label: "Choose..." });
      this.allAdmins.set(newArray);
      const newCSRData = data
        .filter((item) => {
          return item.role === CUSTOMER_SUPPORT_ROLE;
        })
        .map((item) => {
          return {
            value: item.adminId.toString(),
            label: item.fullname
          };
        });
      const newACSRArray = newCSRData.concat({
        value: "",
        label: "Choose..."
      });
      this.allCsrAdmins.set(newACSRArray);
    }
  };
}
const getRootStore = memoize(
  () => new RootStore(),
  () => 1
);
export default getRootStore;
